import { useState, useSyncExternalStore, type FormEvent } from "react";
import { useReverification, useSession, useUser } from "@clerk/react";
import { KeyRound } from "lucide-react";
import QRCode from "qrcode";

import { cn } from "../../lib/utils";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { MFAOpeningState } from "./mfa-opening-state";

type Enrollment = {
  qrDataUrl: string | null;
  secret: string | null;
  uri: string | null;
};

type TOTPSetupResource = {
  backupCodes?: string[];
  secret?: string;
  uri?: string;
  verified: boolean;
};

export type AuthenticatorSetupComplete = {
  backupCodes: string[];
  userId: string;
};

const TOTP_CODE_PATTERN = /^\d{6}$/;

const COARSE_POINTER = "(pointer: coarse)";

/** Touch-primary device (no user-agent sniffing). False where matchMedia is absent. */
function useCoarsePointer(): boolean {
  return useSyncExternalStore(
    (onChange) => {
      if (typeof window === "undefined" || !window.matchMedia) return () => {};
      const mql = window.matchMedia(COARSE_POINTER);
      mql.addEventListener?.("change", onChange);
      return () => mql.removeEventListener?.("change", onChange);
    },
    () =>
      typeof window !== "undefined" && !!window.matchMedia
        ? window.matchMedia(COARSE_POINTER).matches
        : false,
    () => false,
  );
}

export function AuthenticatorSetupPanel({
  cancelLabel = "Cancel",
  className,
  codeInputId = "authenticator-setup-code",
  contentClassName,
  onBusyChange,
  onAuthenticatorStatusChange,
  onCancel,
  onComplete,
  openingDescription = "Confirm in the secure prompt, then Sill will show the QR code.",
  showStartButton = true,
  startButtonClassName,
  startLabel = "Add authenticator app",
  verifyLabel = "Verify and enable",
}: {
  cancelLabel?: string;
  className?: string;
  codeInputId?: string;
  contentClassName?: string;
  /** Optional navigation guard while a credential mutation is in flight. */
  onBusyChange?: (busy: boolean) => void;
  onAuthenticatorStatusChange?: (enabled: boolean, userId: string) => void;
  onCancel?: () => void;
  onComplete?: (result: AuthenticatorSetupComplete) => void;
  openingDescription?: string;
  showStartButton?: boolean;
  startButtonClassName?: string;
  startLabel?: string;
  verifyLabel?: string;
}) {
  const { user } = useUser();
  const { session } = useSession();
  const [enrollment, setEnrollment] = useState<Enrollment | null>(null);
  const [code, setCode] = useState("");
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const isMobile = useCoarsePointer();
  const createVerifiedTOTP = useReverification(async () => user?.createTOTP());
  const canVerify = TOTP_CODE_PATTERN.test(code) && !isVerifying;
  const hasVisibleContent =
    showStartButton ||
    isCreating ||
    enrollment ||
    backupCodes.length > 0 ||
    message ||
    error;

  async function beginEnrollment() {
    if (!user || isCreating) return;

    setIsCreating(true);
    onBusyChange?.(true);
    setError(null);
    setMessage(null);
    setBackupCodes([]);
    try {
      const totp = await createVerifiedTOTP();
      if (!totp) {
        throw new Error("Couldn't start authenticator setup. Try again.");
      }
      setEnrollment(await toEnrollment(totp));
      setShowQr(false);
      setCode("");
    } catch (err) {
      setError(
        clerkErrorMessage(
          err,
          "Couldn't start authenticator setup. Try again.",
        ),
      );
    } finally {
      setIsCreating(false);
      onBusyChange?.(false);
    }
  }

  async function verifyEnrollment(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!user || isVerifying) return;
    if (!TOTP_CODE_PATTERN.test(code)) {
      setError("Enter the 6-digit code from your authenticator app.");
      return;
    }

    setIsVerifying(true);
    onBusyChange?.(true);
    setError(null);
    setMessage(null);
    try {
      const verified = await user.verifyTOTP({ code });
      const nextBackupCodes = verified.backupCodes ?? [];
      setEnrollment(null);
      setCode("");
      setBackupCodes(nextBackupCodes);
      onAuthenticatorStatusChange?.(true, user.id);
      setMessage("Authenticator app added.");
      await reloadAuthState(user, session ?? null);
      onComplete?.({ backupCodes: nextBackupCodes, userId: user.id });
    } catch (err) {
      setError(
        clerkErrorMessage(
          err,
          "Couldn't verify that code. Check the code and try again.",
        ),
      );
    } finally {
      setIsVerifying(false);
      onBusyChange?.(false);
    }
  }

  if (!hasVisibleContent) return null;

  return (
    <div className={cn("space-y-4", className)}>
      {showStartButton && !enrollment && backupCodes.length === 0 ? (
        <Button
          className={cn("w-full sm:w-auto", startButtonClassName)}
          disabled={!user}
          loading={isCreating}
          onClick={() => void beginEnrollment()}
          size="sm"
        >
          <KeyRound className="h-4 w-4" />
          {startLabel}
        </Button>
      ) : null}

      {isCreating ? (
        <div className={contentClassName}>
          <MFAOpeningState
            description={openingDescription}
            title="Opening authenticator setup"
          />
        </div>
      ) : null}

      {enrollment ? (
        <form
          className={cn(
            "ph-no-capture surface-muted grid w-full gap-5 p-4",
            contentClassName,
          )}
          onSubmit={verifyEnrollment}
        >
          <div
            className={cn(
              "grid gap-4",
              !(isMobile && !showQr && enrollment.uri) &&
                "md:grid-cols-[12rem_minmax(0,1fr)]",
            )}
          >
            {!isMobile || showQr || !enrollment.uri ? (
              <div className="flex min-h-48 items-center justify-center rounded-xl bg-background p-3 shadow-sm">
                {enrollment.qrDataUrl ? (
                  <img
                    alt="Authenticator app QR code"
                    className="h-40 w-40"
                    src={enrollment.qrDataUrl}
                  />
                ) : (
                  <p className="text-center text-sm text-muted-foreground">
                    Use the manual setup key.
                  </p>
                )}
              </div>
            ) : null}
            <div className="space-y-4">
              <div>
                <p className="text-sm font-semibold text-foreground">
                  {isMobile && enrollment.uri
                    ? "Add to your authenticator app"
                    : "Scan the QR code"}
                </p>
                <p className="mt-1 text-sm leading-6 text-muted-foreground">
                  Open your authenticator app, add a new account, then enter the
                  6-digit code it shows for Sill.
                </p>
              </div>
              {enrollment.uri ? (
                <div className="space-y-2">
                  {isMobile ? (
                    <Button asChild size="sm">
                      <a href={enrollment.uri}>Add to authenticator app</a>
                    </Button>
                  ) : (
                    <a
                      className="text-sm font-medium text-foreground underline"
                      href={enrollment.uri}
                    >
                      Add to authenticator app
                    </a>
                  )}
                  <p className="text-xs text-muted-foreground">
                    {isMobile
                      ? "Opens your phone's password or authenticator app."
                      : "Or open in an authenticator app on this device."}
                  </p>
                  {isMobile ? (
                    <button
                      className="text-sm text-muted-foreground underline"
                      type="button"
                      onClick={() => setShowQr((v) => !v)}
                    >
                      {showQr ? "Hide QR code" : "Show QR code instead"}
                    </button>
                  ) : null}
                </div>
              ) : null}
              {enrollment.secret ? (
                <div className="space-y-2">
                  <p className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">
                    Manual setup key
                  </p>
                  <code className="block break-all rounded-xl bg-background px-3 py-2 font-mono text-sm text-foreground">
                    {formatSecret(enrollment.secret)}
                  </code>
                </div>
              ) : null}
            </div>
          </div>

          <Input
            autoComplete="one-time-code"
            description="Use the current 6-digit code from your authenticator app."
            error={
              code && !TOTP_CODE_PATTERN.test(code)
                ? "Enter exactly 6 digits."
                : null
            }
            id={codeInputId}
            inputMode="numeric"
            label="Verification code"
            maxLength={6}
            onChange={(event) =>
              setCode(event.target.value.replace(/\D/g, "").slice(0, 6))
            }
            pattern="\d{6}"
            value={code}
          />

          <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
            <Button
              disabled={isVerifying}
              onClick={() => {
                setEnrollment(null);
                setCode("");
                setError(null);
                onCancel?.();
              }}
              type="button"
              variant="outline"
            >
              {cancelLabel}
            </Button>
            <Button disabled={!canVerify} loading={isVerifying} type="submit">
              {verifyLabel}
            </Button>
          </div>
        </form>
      ) : null}

      {backupCodes.length > 0 ? (
        <div
          className={cn("ph-no-capture surface-muted p-4", contentClassName)}
          role="status"
        >
          <p className="text-sm font-semibold text-foreground">Backup codes</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Store these somewhere safe. They may not be shown again.
          </p>
          <ul className="mt-3 grid gap-2 sm:grid-cols-2">
            {backupCodes.map((backupCode) => (
              <li
                className="rounded-xl bg-background px-3 py-2 font-mono text-sm text-foreground"
                key={backupCode}
              >
                {backupCode}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {message ? (
        <p
          className={cn("text-sm text-muted-foreground", contentClassName)}
          role="status"
        >
          {message}
        </p>
      ) : null}
      {error ? (
        <p
          className={cn("text-sm text-destructive", contentClassName)}
          role="alert"
        >
          {error}
        </p>
      ) : null}
    </div>
  );
}

async function toEnrollment(totp: TOTPSetupResource): Promise<Enrollment> {
  return {
    qrDataUrl: totp.uri
      ? await QRCode.toDataURL(totp.uri, { margin: 1, width: 192 })
      : null,
    secret: totp.secret ?? null,
    uri: totp.uri ?? null,
  };
}

async function reloadAuthState(
  user: { reload: () => Promise<unknown> },
  session: { reload: () => Promise<unknown> } | null,
) {
  await Promise.all([
    user.reload().catch(() => undefined),
    session?.reload().catch(() => undefined),
  ]);
}

function formatSecret(secret: string) {
  return secret
    .replace(/\s+/g, "")
    .replace(/(.{4})/g, "$1 ")
    .trim();
}

function clerkErrorMessage(err: unknown, fallback: string) {
  if (typeof err === "object" && err !== null && "errors" in err) {
    const errors = (
      err as { errors?: Array<{ longMessage?: string; message?: string }> }
    ).errors;
    const message = errors?.[0]?.longMessage ?? errors?.[0]?.message;
    if (message) return message;
  }
  if (err instanceof Error && err.message) {
    return err.message;
  }
  return fallback;
}
